import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SquareIcon from "@mui/icons-material/Square";
import { Alert, Box, Checkbox, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { playerSummaryName } from "../players/utils";
import { FormActions } from "../utils/FormActions";
const ScoreInput = ({ value, onChange, disabled, readOnly, label, }) => {
    const currentValue = value ?? 0;
    if (readOnly) {
        return (_jsxs(Stack, { alignItems: "center", spacing: 0.5, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", sx: {
                        fontWeight: 500,
                        maxWidth: { xs: 80, sm: 120 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                    }, children: label }), value !== undefined && (_jsx(Typography, { fontWeight: 600, sx: { fontSize: { xs: "1.75rem", sm: "2.5rem" }, px: 1 }, children: value }))] }));
    }
    const handleInputChange = (e) => {
        const val = e.target.value;
        if (val === "") {
            onChange(0);
        }
        else {
            const parsed = parseInt(val, 10);
            if (!isNaN(parsed) && parsed >= 0) {
                onChange(parsed);
            }
        }
    };
    return (_jsxs(Stack, { alignItems: "center", spacing: 0.5, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", sx: {
                    fontWeight: 500,
                    maxWidth: { xs: 80, sm: 120 },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                }, children: label }), _jsxs(Stack, { direction: "row", alignItems: "center", spacing: { xs: 0.5, sm: 1 }, children: [_jsx(IconButton, { size: "small", onClick: () => onChange(Math.max(0, currentValue - 1)), disabled: disabled || currentValue <= 0, sx: {
                            bgcolor: "action.hover",
                            "&:hover": { bgcolor: "action.selected" },
                        }, children: _jsx(RemoveIcon, { fontSize: "small" }) }), _jsx(TextField, { value: currentValue, onChange: handleInputChange, disabled: disabled, type: "number", inputProps: {
                            min: 0,
                            style: {
                                textAlign: "center",
                                fontWeight: 600,
                                padding: "4px 0",
                                MozAppearance: "textfield",
                            },
                        }, sx: {
                            width: { xs: 50, sm: 70 },
                            "& input": {
                                fontSize: { xs: "1.75rem", sm: "2.5rem" },
                            },
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": { border: "none" },
                            },
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                WebkitAppearance: "none",
                                margin: 0,
                            },
                        } }), _jsx(IconButton, { size: "small", onClick: () => onChange(currentValue + 1), disabled: disabled, sx: {
                            bgcolor: "action.hover",
                            "&:hover": { bgcolor: "action.selected" },
                        }, children: _jsx(AddIcon, { fontSize: "small" }) })] })] }));
};
export const CompactStatInput = ({ value, onChange, disabled, max, }) => (_jsx(TextField, { value: value ?? "", onChange: (e) => {
        const val = e.target.value;
        if (val === "") {
            onChange(undefined);
            return;
        }
        const n = parseInt(val, 10);
        onChange(max !== undefined ? Math.min(n, max) : n);
    }, type: "number", size: "small", disabled: disabled, inputProps: { min: 0, max, style: { textAlign: "center" } }, sx: { width: { xs: 48, sm: 60 } } }));
// The Player / Played / goals / yellow / red column header for a stats table,
// shared by the single-match form and the bulk gameweek-results form so the two
// can't drift. The row bodies differ (different react-hook-form field paths), so
// only the header chrome is shared.
export const StatsTableHeader = ({ showPlayedCheckbox = true, }) => (_jsx(TableHead, { children: _jsxs(TableRow, { sx: { bgcolor: "action.hover" }, children: [_jsx(TableCell, { sx: { fontWeight: 600, minWidth: 100 }, children: "Player" }), showPlayedCheckbox && (_jsx(TableCell, { align: "center", sx: { width: { xs: 56, sm: 70 }, px: { xs: 0.5, sm: 1 } }, children: _jsx(Typography, { variant: "caption", fontWeight: 600, color: "text.secondary", noWrap: true, children: "Played" }) })), _jsx(TableCell, { align: "center", sx: { width: { xs: 56, sm: 70 }, px: { xs: 0.5, sm: 1 } }, children: _jsx(Tooltip, { title: "Goals", children: _jsx(SportsSoccerIcon, { fontSize: "small", color: "action" }) }) }), _jsx(TableCell, { align: "center", sx: { width: { xs: 56, sm: 70 }, px: { xs: 0.5, sm: 1 } }, children: _jsx(Tooltip, { title: "Yellow Cards", children: _jsx(SquareIcon, { fontSize: "small", sx: { color: "#ffc107" } }) }) }), _jsx(TableCell, { align: "center", sx: { width: { xs: 56, sm: 70 }, px: { xs: 0.5, sm: 1 } }, children: _jsx(Tooltip, { title: "Red Cards", children: _jsx(SquareIcon, { fontSize: "small", sx: { color: "#dc3545" } }) }) })] }) }));
const TeamStatsTable = ({ teamName, players, control, setValue, startIndex, canModify, showPlayedCheckbox, duplicatePlayerIds, }) => {
    if (players.length === 0)
        return null;
    return (_jsxs(Paper, { variant: "outlined", sx: { overflow: "hidden" }, children: [_jsx(Box, { sx: { bgcolor: "primary.main", color: "primary.contrastText", p: 1.5 }, children: _jsx(Typography, { variant: "subtitle1", fontWeight: 600, noWrap: true, children: teamName }) }), _jsx(Box, { sx: { overflowX: "auto" }, children: _jsxs(Table, { size: "small", sx: { minWidth: { xs: 320, sm: "auto" } }, children: [_jsx(StatsTableHeader, { showPlayedCheckbox: showPlayedCheckbox }), _jsx(TableBody, { children: players.map((player, i) => {
                                const idx = startIndex + i;
                                const isDuplicate = duplicatePlayerIds.has(player.id);
                                return (_jsxs(TableRow, { sx: {
                                        "&:last-child td": { borderBottom: 0 },
                                        ...(isDuplicate && {
                                            bgcolor: "error.light",
                                            "& td": { color: "error.contrastText" },
                                        }),
                                    }, children: [_jsx(TableCell, { sx: { py: { xs: 1, sm: 1.5 } }, children: _jsx(Typography, { noWrap: true, children: playerSummaryName(player) }) }), showPlayedCheckbox && (_jsx(TableCell, { align: "center", sx: { px: { xs: 0.5, sm: 1 }, py: { xs: 1, sm: 1.5 } }, children: _jsx(Controller, { name: `playerStats.${idx}.played`, control: control, render: ({ field }) => (_jsx(Checkbox, { checked: field.value ?? true, onChange: (e) => {
                                                        const isPlayed = e.target.checked;
                                                        field.onChange(isPlayed);
                                                        // Clear stats when player is deselected
                                                        if (!isPlayed) {
                                                            setValue(`playerStats.${idx}.goalsScored`, undefined);
                                                            setValue(`playerStats.${idx}.yellowCards`, undefined);
                                                            setValue(`playerStats.${idx}.redCards`, undefined);
                                                        }
                                                    }, disabled: !canModify, size: "small" })) }) })), _jsx(TableCell, { align: "center", sx: { px: { xs: 0.5, sm: 1 }, py: { xs: 1, sm: 1.5 } }, children: _jsx(Controller, { name: `playerStats.${idx}.goalsScored`, control: control, render: ({ field }) => (_jsx(CompactStatInput, { value: field.value, onChange: (val) => {
                                                        field.onChange(val);
                                                        // Auto-mark as played when stat is entered
                                                        if (showPlayedCheckbox &&
                                                            val !== undefined &&
                                                            val > 0) {
                                                            setValue(`playerStats.${idx}.played`, true);
                                                        }
                                                    }, disabled: !canModify })) }) }), _jsx(TableCell, { align: "center", sx: { px: { xs: 0.5, sm: 1 }, py: { xs: 1, sm: 1.5 } }, children: _jsx(Controller, { name: `playerStats.${idx}.yellowCards`, control: control, render: ({ field }) => (_jsx(CompactStatInput, { value: field.value, onChange: (val) => {
                                                        field.onChange(val);
                                                        if (showPlayedCheckbox &&
                                                            val !== undefined &&
                                                            val > 0) {
                                                            setValue(`playerStats.${idx}.played`, true);
                                                        }
                                                    }, disabled: !canModify })) }) }), _jsx(TableCell, { align: "center", sx: { px: { xs: 0.5, sm: 1 }, py: { xs: 1, sm: 1.5 } }, children: _jsx(Controller, { name: `playerStats.${idx}.redCards`, control: control, render: ({ field }) => (_jsx(CompactStatInput, { value: field.value, onChange: (val) => {
                                                        field.onChange(val);
                                                        if (showPlayedCheckbox &&
                                                            val !== undefined &&
                                                            val > 0) {
                                                            setValue(`playerStats.${idx}.played`, true);
                                                        }
                                                    }, disabled: !canModify })) }) })] }, player?.id));
                            }) })] }) })] }));
};
export const MatchStatsForm = ({ leftTeamName, rightTeamName, leftTeamPlayers, rightTeamPlayers, initialLeftScore, initialRightScore, initialPlayerStats, onSubmit, canModifyLeftTeam = true, canModifyRightTeam = true, canModifyResult = true, showPlayedCheckbox = false, showMatchReport = false, initialMatchReport, isSubmitting = false, }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    // Custom validation for duplicate players
    const validateNoDuplicatePlayers = (playerStats) => {
        if (!showPlayedCheckbox)
            return true;
        const playerTeams = new Map();
        playerStats?.forEach((stat) => {
            if (stat.played !== false) {
                if (!playerTeams.has(stat.playerId)) {
                    playerTeams.set(stat.playerId, new Set());
                }
                playerTeams.get(stat.playerId).add(stat.teamId);
            }
        });
        let hasDuplicates = false;
        playerTeams.forEach((teams) => {
            if (teams.size > 1) {
                hasDuplicates = true;
            }
        });
        return hasDuplicates
            ? "A player cannot play for both teams. Please uncheck one of the highlighted players."
            : true;
    };
    const { control, handleSubmit, watch, setValue, formState: { errors }, } = useForm({
        defaultValues: {
            // Kept undefined when there's no result so a read-only viewer (a user who
            // can't set the result) sees the "vs" state, not a misleading 0. Editors
            // still see an editable 0 (ScoreInput renders `value ?? 0`) and submit 0
            // if untouched (coerced in onFormSubmit).
            leftTeamScore: initialLeftScore,
            rightTeamScore: initialRightScore,
            matchReport: initialMatchReport ?? "",
            playerStats: initialPlayerStats,
        },
    });
    // useWatch triggers re-renders on array value changes (unlike watch)
    const playerStats = useWatch({ control, name: "playerStats" });
    const leftScore = watch("leftTeamScore");
    const rightScore = watch("rightTeamScore");
    const matchReportValue = watch("matchReport") ?? "";
    // A match with no result shown to someone who can't set it: "vs", not "—".
    const scoreUnset = !canModifyResult && leftScore === undefined && rightScore === undefined;
    // Find players who are marked as "played" on multiple teams (for highlighting)
    const duplicatePlayerIds = useMemo(() => {
        if (!showPlayedCheckbox)
            return new Set();
        const playerTeams = new Map();
        playerStats?.forEach((stat) => {
            if (stat.played !== false) {
                if (!playerTeams.has(stat.playerId)) {
                    playerTeams.set(stat.playerId, new Set());
                }
                playerTeams.get(stat.playerId).add(stat.teamId);
            }
        });
        const duplicates = new Set();
        playerTeams.forEach((teams, playerId) => {
            if (teams.size > 1) {
                duplicates.add(playerId);
            }
        });
        return duplicates;
    }, [showPlayedCheckbox, playerStats]);
    const hasDuplicatePlayers = duplicatePlayerIds.size > 0;
    const onFormSubmit = (data) => {
        // Run validation before submitting
        const validationResult = validateNoDuplicatePlayers(data.playerStats);
        if (validationResult !== true) {
            return;
        }
        onSubmit({
            // An editor who never touched the score still records 0–0 (score isn't
            // optional once you can set it); the undefined default only drives the
            // read-only "vs" display.
            leftTeamScore: data.leftTeamScore ?? 0,
            rightTeamScore: data.rightTeamScore ?? 0,
            matchReport: data.matchReport,
        }, data.playerStats);
    };
    const canModifyAny = canModifyLeftTeam || canModifyRightTeam;
    return (_jsxs(Box, { component: "form", onSubmit: handleSubmit(onFormSubmit), noValidate: true, children: [_jsx(Paper, { elevation: 2, sx: {
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    bgcolor: "background.paper",
                }, children: _jsxs(Stack, { direction: "row", justifyContent: "center", alignItems: "center", spacing: { xs: 2, sm: 4 }, children: [_jsx(ScoreInput, { value: leftScore, onChange: (val) => setValue("leftTeamScore", val), readOnly: !canModifyResult, label: leftTeamName }), _jsx(Typography, { variant: "h4", color: "text.secondary", sx: { px: { xs: 1, sm: 2 } }, children: scoreUnset ? "vs" : "—" }), _jsx(ScoreInput, { value: rightScore, onChange: (val) => setValue("rightTeamScore", val), readOnly: !canModifyResult, label: rightTeamName })] }) }), (leftTeamPlayers.length > 0 || rightTeamPlayers.length > 0) && (_jsxs(Stack, { direction: isMobile ? "column" : "row", spacing: 2, sx: { mb: 3 }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(TeamStatsTable, { teamName: leftTeamName, players: leftTeamPlayers, control: control, setValue: setValue, startIndex: 0, canModify: canModifyLeftTeam, showPlayedCheckbox: showPlayedCheckbox, duplicatePlayerIds: duplicatePlayerIds }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(TeamStatsTable, { teamName: rightTeamName, players: rightTeamPlayers, control: control, setValue: setValue, startIndex: leftTeamPlayers.length, canModify: canModifyRightTeam, showPlayedCheckbox: showPlayedCheckbox, duplicatePlayerIds: duplicatePlayerIds }) })] })), showMatchReport && (_jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Controller, { name: "matchReport", control: control, rules: {
                            maxLength: {
                                value: 3000,
                                message: "Max 3000 characters allowed",
                            },
                        }, render: ({ field }) => (_jsx(TextField, { ...field, label: "Match Report", placeholder: "Add a match report (optional)", multiline: true, minRows: 3, fullWidth: true, disabled: !canModifyAny, error: !!errors.matchReport, helperText: errors.matchReport?.message || "" })) }), _jsxs(Typography, { variant: "caption", align: "right", color: matchReportValue.length > 3000 ? "error" : "textSecondary", display: "block", children: [matchReportValue.length, " / 3000 characters"] })] })), hasDuplicatePlayers && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: "A player cannot play for both teams. Please uncheck one of the highlighted players." })), canModifyAny && (_jsx(FormActions, { submitLabel: "Save Match Stats", isSubmitting: isSubmitting, submitDisabled: hasDuplicatePlayers }))] }));
};
